/**
 * Free Destination Guide PDF download — destination-page variant.
 *
 * Uses the public `/api/public/destination-guide/pdf?dest=<slug>` endpoint
 * which renders a multilingual PDF from the curated destination registry.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Download, FileText, Loader2 } from "lucide-react";

interface Props {
  destSlug: string;
  destinationName: string;
}

type Lang = "es" | "en" | "fr" | "pt" | "it";

const LABELS: Record<Lang, { title: string; desc: string; cta: string; downloading: string; subline: string }> = {
  es: {
    title: "📥 Guía Gratuita del Destino",
    desc: "Llévate gratis una guía completa de este destino con información útil para tu viaje.",
    cta: "📥 Descargar Guía PDF",
    downloading: "Generando guía…",
    subline: "Sin registro · Sin pago · Lista para imprimir",
  },
  en: {
    title: "📥 Free Destination Guide",
    desc: "Take home a free, complete guide of this destination with helpful information for your trip.",
    cta: "📥 Download PDF Guide",
    downloading: "Generating guide…",
    subline: "No sign-up · No payment · Ready to print",
  },
  fr: {
    title: "📥 Guide Gratuit de la Destination",
    desc: "Emporte un guide complet et gratuit de cette destination, avec des infos utiles pour ton voyage.",
    cta: "📥 Télécharger le Guide PDF",
    downloading: "Génération du guide…",
    subline: "Sans inscription · Sans paiement · Prêt à imprimer",
  },
  pt: {
    title: "📥 Guia Gratuito do Destino",
    desc: "Leve grátis um guia completo deste destino com informações úteis para sua viagem.",
    cta: "📥 Baixar Guia PDF",
    downloading: "Gerando o guia…",
    subline: "Sem cadastro · Sem pagamento · Pronto para imprimir",
  },
  it: {
    title: "📥 Guida Gratuita della Destinazione",
    desc: "Porta con te una guida completa e gratuita di questa destinazione, con informazioni utili per il tuo viaggio.",
    cta: "📥 Scarica la Guida PDF",
    downloading: "Generazione della guida…",
    subline: "Senza registrazione · Senza pagamento · Pronta da stampare",
  },
};

function pickLang(raw: string): Lang {
  const v = (raw || "").slice(0, 2).toLowerCase();
  if (v === "en" || v === "fr" || v === "pt" || v === "it") return v;
  return "es";
}

function buildFilename(name: string, lang: Lang): string {
  const slug = name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  switch (lang) {
    case "en": return `mostrando-mexico-guide-${slug}-en.pdf`;
    case "fr": return `guide-mostrando-mexique-${slug}-fr.pdf`;
    case "pt": return `guia-mostrando-mexico-${slug}-pt.pdf`;
    case "it": return `guida-mostrando-mexico-${slug}-it.pdf`;
    case "es":
    default:   return `guia-mostrando-mexico-${slug}-es.pdf`;
  }
}

export function DestinationGuidePdfDownload({ destSlug, destinationName }: Props) {
  const { i18n } = useTranslation();
  const lang = pickLang(i18n.language);
  const labels = LABELS[lang];
  const [busy, setBusy] = useState(false);

  async function handleDownload() {
    setBusy(true);
    try {
      const url = `/api/public/destination-guide/pdf?dest=${encodeURIComponent(destSlug)}&lang=${lang}`;
      // The server returns 503 while the guide is still being fully
      // translated into the active language; retry once.
      let resp = await fetch(url, { credentials: "omit" });
      if (resp.status === 503) {
        await new Promise((r) => setTimeout(r, 1500));
        resp = await fetch(url, { credentials: "omit" });
      }
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = buildFilename(destinationName, lang);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 4000);
    } catch (e) {
      console.error("[dest-guide-download]", e);
      window.open(
        `/api/public/destination-guide/pdf?dest=${encodeURIComponent(destSlug)}&lang=${lang}`,
        "_blank",
        "noopener",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <section
      aria-labelledby="dest-guide-heading"
      className="relative mt-10 overflow-hidden rounded-2xl border border-primary/25 bg-gradient-to-br from-primary/10 via-card to-accent/10 p-6 shadow-sm sm:p-8"
    >
      <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-primary/15 blur-2xl" />
      <div className="pointer-events-none absolute -bottom-12 -left-10 h-48 w-48 rounded-full bg-accent/15 blur-3xl" />
      <div className="relative flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-4">
          <div className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-primary text-primary-foreground shadow-md">
            <FileText className="h-7 w-7" />
          </div>
          <div className="min-w-0">
            <h2 id="dest-guide-heading" className="font-display text-2xl font-semibold leading-tight text-foreground">
              {labels.title}
            </h2>
            <p className="mt-1 text-sm text-foreground/80 sm:text-base">{labels.desc}</p>
            <p className="mt-1 text-xs text-muted-foreground">{labels.subline}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleDownload}
          disabled={busy}
          className="inline-flex h-12 shrink-0 items-center justify-center gap-2 rounded-full bg-primary px-6 text-sm font-semibold text-primary-foreground shadow-md transition hover:opacity-90 disabled:cursor-wait disabled:opacity-70 sm:text-base"
        >
          {busy ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" /> {labels.downloading}
            </>
          ) : (
            <>
              <Download className="h-5 w-5" /> {labels.cta}
            </>
          )}
        </button>
      </div>
    </section>
  );
}
